// fallow-ignore-file code-duplication
import React, { useState, useMemo, useEffect, useRef } from 'react';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  MultiSelect,
  MultiSelectOption,
} from '@/components/common/MultiSelect';
import { SingleSelect } from '@/components/common/SingleSelect';
import GentlyTable, { type TableColumn } from '@/components/common/GentlyTable';
import { useTableSearch } from '@/components/common/hooks/useTableSearch';
import { useTableSort } from '@/components/common/hooks/useTableSort';
import { useSupplierTagSummary } from '@/api/rebates/useReports';
import { useSupplierTags } from '@/api/rebates/useSupplierTags';
import useDivisions from '@/api/organization/useDivisions';
import useRebateAgreementYears from '@/api/rebates/useRebateAgreementYears';
import { TableSkeleton } from '@/components/common/loading-skeletons/table-skeleton';
import type {
  ReportLineType,
  ReportTable,
  ReportTimeFilter,
} from '@erp/shared-types';
import { LINE_TYPE_DESCRIPTIONS } from '@/types/rebateAnalytics';
import {
  ShareNetworkIcon,
  LinkIcon,
  FileCsvIcon,
  FileXlsIcon,
} from '@phosphor-icons/react';
import { downloadRebateRecordsXlsx } from '@/components/rebates/reports/export/downloadRebateReportXlsx';
import { reportTableToDownloadOptions } from '@/components/rebates/reports/export/reportTableToDownloadOptions';
import type { DownloadCSVOptions } from '@/utils/csvDownload';
import { ShareReportModal } from 'src/components/reports/ShareReportModal';
import { SharedLinksManager } from 'src/components/reports/SharedLinksManager';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { T, Var, useGT } from 'gt-react';
import { HeaderPageTitle } from 'src/context/header-title.context';

const REPORT_ID = 'supplier-tag-summary';
const SUPPLIER_TAG_SEARCH_FIELDS: (keyof Record<string, unknown>)[] = [
  '_label',
];

type SupplierTagRow = Record<string, unknown>;

function supplierTagCellValue(value: unknown) {
  if (
    typeof value === 'object' &&
    value !== null &&
    'formattedValue' in value
  ) {
    return (
      <span className="text-sm font-medium">
        {(value as { formattedValue: string }).formattedValue}
      </span>
    );
  }
  return <span className="text-sm">{(value as string) || '-'}</span>;
}

function supplierTagColumns(
  downloadOptions: DownloadCSVOptions,
): TableColumn<SupplierTagRow>[] {
  const mapped: TableColumn<SupplierTagRow>[] = (
    downloadOptions.columns ?? []
  ).map((column) => ({
    id: column.id,
    label: column.label,
    sortable: true,
    minWidth: '100px',
    align: 'right',
  }));
  const labelColumn = mapped[0];
  if (labelColumn) {
    labelColumn.minWidth = '200px';
    labelColumn.align = 'left';
    labelColumn.render = (value: unknown) => (
      <span className="font-medium text-sm">{value as string}</span>
    );
  }
  return mapped;
}

const SupplierTagSortContext = React.createContext({
  sortedData: [] as SupplierTagRow[],
  handleSort: ((_columnId: keyof SupplierTagRow, _sortable?: boolean) => {
    return;
  }) as (columnId: keyof SupplierTagRow, sortable?: boolean) => void,
  sortBy: null as keyof SupplierTagRow | null,
  sortDirection: 'desc' as 'asc' | 'desc',
  searchQuery: '',
  setSearchQuery: ((_value: string) => {
    return;
  }) as (value: string) => void,
});

function SupplierTagSortProvider({
  data,
  columns,
  defaultSort,
  children,
}: {
  data: SupplierTagRow[];
  columns: TableColumn<SupplierTagRow>[];
  defaultSort: {
    sortBy: keyof SupplierTagRow;
    sortDirection: 'desc';
  };
  children: React.ReactNode;
}) {
  const { searchQuery, setSearchQuery, filteredData } = useTableSearch({
    data,
    searchFields: SUPPLIER_TAG_SEARCH_FIELDS,
  });
  const sort = useTableSort({
    data: filteredData,
    columns,
    defaultSort,
  });
  return (
    <SupplierTagSortContext.Provider
      value={{ ...sort, searchQuery, setSearchQuery }}
    >
      {children}
    </SupplierTagSortContext.Provider>
  );
}

function SupplierTagExcelButton({
  downloadOptions,
}: {
  downloadOptions: DownloadCSVOptions;
}) {
  const { sortedData } = React.useContext(SupplierTagSortContext);
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => {
        if (sortedData.length === 0) return;
        void downloadRebateRecordsXlsx({
          ...downloadOptions,
          data: sortedData,
        });
      }}
      disabled={sortedData.length === 0}
    >
      <FileXlsIcon className="h-4 w-4 mr-2" />
      <T context="button to download report as Excel">Download Excel</T>
    </Button>
  );
}

function SupplierTagReportTable({
  table,
  tableName,
  timeFilter,
  onTimeFilterChange,
  downloadFilename,
  timeFilterOptions,
  timePeriodPlaceholder,
  searchPlaceholder,
  columns,
  defaultSort,
}: {
  table: ReportTable;
  tableName?: string;
  timeFilter: ReportTimeFilter;
  onTimeFilterChange: (filter: ReportTimeFilter) => void;
  downloadFilename: string;
  timeFilterOptions: { id: string; label: string }[];
  timePeriodPlaceholder: string;
  searchPlaceholder: string;
  columns: TableColumn<SupplierTagRow>[];
  defaultSort: {
    sortBy: keyof SupplierTagRow;
    sortDirection: 'desc';
  };
}) {
  const {
    sortedData,
    handleSort,
    sortBy,
    sortDirection,
    searchQuery,
    setSearchQuery,
  } = React.useContext(SupplierTagSortContext);
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <CardTitle className="text-lg font-medium">
            {tableName ?? table.title}
          </CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              <T context="report table filter label">Time Period:</T>
            </span>
            <SingleSelect
              options={timeFilterOptions}
              value={
                timeFilterOptions.find((opt) => opt.id === timeFilter) ?? null
              }
              onChange={(option) => {
                onTimeFilterChange(
                  (option?.id ?? timeFilter) as ReportTimeFilter,
                );
              }}
              placeholder={timePeriodPlaceholder}
              size="sm"
              fullWidth={false}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="px-6 pb-6">
          <GentlyTable
            data={sortedData}
            columns={columns}
            showSearch={true}
            searchPlaceholder={searchPlaceholder}
            searchFields={SUPPLIER_TAG_SEARCH_FIELDS}
            serverSideSearch={{
              value: searchQuery,
              onChange: setSearchQuery,
            }}
            serverSideSorting={{
              sortBy: sortBy ? String(sortBy) : '',
              sortDirection,
              onSort: (columnId) => {
                handleSort(columnId as keyof SupplierTagRow, true);
              },
            }}
            showDownload={true}
            downloadFilename={downloadFilename}
            downloadIcon={<FileCsvIcon className="h-4 w-4" />}
            showCount={true}
            defaultSort={defaultSort}
            defaultCellRender={supplierTagCellValue}
          />
        </div>
      </CardContent>
    </Card>
  );
}

export interface SupplierTagReportPageProps {
  lockedFilters?: {
    year: number;
    divisionIds?: string[];
    supplierTagIds?: string[];
    lineType?: ReportLineType;
  };
}

export const SupplierTagReportPage: React.FC<SupplierTagReportPageProps> = ({
  lockedFilters,
}) => {
  const gt = useGT();
  const currentYear = new Date().getFullYear();

  const isShared = !!lockedFilters;
  const reportTitle = gt('Supplier Tag Summary', {
    context: 'report name, supplier tag rebate summary',
  });
  const [selectedYear, setSelectedYear] = useState<number>(
    lockedFilters?.year ?? currentYear,
  );
  const [selectedDivisions, setSelectedDivisions] = useState<
    MultiSelectOption[]
  >([]);
  const [selectedSupplierTag, setSelectedSupplierTag] =
    useState<MultiSelectOption | null>(null);
  const [lineType, setLineType] = useState<ReportLineType>('reported');
  // Default time filter based on year: 'current' for current year, 'all' for past years
  const [timeFilter, setTimeFilter] = useState<ReportTimeFilter>(
    currentYear === new Date().getFullYear() ? 'current' : 'all',
  );
  const hasSetDefaultDivision = useRef(false);
  const hasSetDefaultSupplierTag = useRef(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [linksManagerOpen, setLinksManagerOpen] = useState(false);

  // Fetch divisions for filter
  const { data: divisionsData, isLoading: divisionsLoading } =
    useDivisions(true);

  // Fetch supplier tags
  const { data: supplierTags, isLoading: supplierTagsLoading } =
    useSupplierTags();

  // Fetch available years for the year selector
  const { data: availableYearsData } = useRebateAgreementYears();
  const availableYears =
    availableYearsData && 'years' in availableYearsData
      ? availableYearsData.years
      : undefined;

  // Prepare division options
  const divisionOptions: MultiSelectOption[] = useMemo(() => {
    if (!divisionsData?.divisions) return [];
    return divisionsData.divisions.map((division) => ({
      id: division.id,
      label: division.name,
      value: division,
      group: division.tags?.[0]?.name,
    }));
  }, [divisionsData?.divisions]);

  // Prepare supplier tag options
  const supplierTagOptions: MultiSelectOption[] = useMemo(() => {
    if (!supplierTags) return [];

    return supplierTags.map((tag) => ({
      id: tag.id,
      label: tag.name,
      value: tag,
    }));
  }, [supplierTags]);

  // Prepare year options - descending order, no future years
  const yearOptions = useMemo(() => {
    if (!availableYears || availableYears.length === 0) {
      // Fallback to last 5 years if no years available
      const years: number[] = [];
      for (let i = 0; i < 5; i++) {
        years.push(currentYear - i);
      }
      return years.map((year) => ({
        id: year.toString(),
        label: year.toString(),
        value: year,
      }));
    }
    return availableYears
      .filter((year) => year <= currentYear) // Exclude future years
      .sort((a, b) => b - a) // Descending order
      .map((year) => ({
        id: year.toString(),
        label: year.toString(),
        value: year,
      }));
  }, [availableYears, currentYear]);

  // Default to ALL divisions when divisions are loaded (only once)
  useEffect(() => {
    if (
      divisionOptions.length > 0 &&
      selectedDivisions.length === 0 &&
      !divisionsLoading &&
      !hasSetDefaultDivision.current
    ) {
      setSelectedDivisions([...divisionOptions]);
      hasSetDefaultDivision.current = true;
    }
  }, [divisionOptions, selectedDivisions.length, divisionsLoading]);

  // Default to first supplier tag option when supplier tags are loaded (only once)
  useEffect(() => {
    if (
      supplierTagOptions.length > 0 &&
      selectedSupplierTag === null &&
      !hasSetDefaultSupplierTag.current
    ) {
      setSelectedSupplierTag(supplierTagOptions[0]);
      hasSetDefaultSupplierTag.current = true;
    }
  }, [supplierTagOptions, selectedSupplierTag]);

  // Update time filter when year changes
  useEffect(() => {
    const defaultTimeFilter: ReportTimeFilter =
      selectedYear < currentYear ? 'all' : 'current';
    setTimeFilter(defaultTimeFilter);
  }, [selectedYear, currentYear]);

  // Prepare line type options
  const lineTypeOptions = useMemo(() => {
    return (['estimates', 'predictions', 'reported'] as const).map((type) => {
      const label =
        type === 'estimates'
          ? gt('Estimates', {
              context: 'rebate report line type option label',
            })
          : type === 'predictions'
            ? gt('Predictions', {
                context: 'rebate report line type option label',
              })
            : gt('Reported', {
                context: 'rebate report line type option label',
              });
      return {
        id: type,
        label,
        value: type,
      };
    });
  }, [gt]);

  const effectiveYear = lockedFilters?.year ?? selectedYear;
  const effectiveDivisionIds =
    lockedFilters?.divisionIds ??
    (selectedDivisions.length > 0
      ? selectedDivisions.map((d) => d.id)
      : undefined);
  const effectiveSupplierTagIds =
    lockedFilters?.supplierTagIds ??
    (selectedSupplierTag ? [selectedSupplierTag.id] : undefined);
  const effectiveLineType = lockedFilters?.lineType ?? lineType;
  if (effectiveLineType !== 'predictions' && timeFilter === 'all') {
    setTimeFilter('current');
  }
  const effectiveTimeFilter: ReportTimeFilter =
    effectiveLineType !== 'predictions' && timeFilter === 'all'
      ? 'current'
      : timeFilter;

  // Fetch report data
  const {
    data: report,
    isLoading,
    error,
  } = useSupplierTagSummary({
    year: effectiveYear,
    divisionIds: effectiveDivisionIds,
    supplierTagIds: effectiveSupplierTagIds,
    lineType: effectiveLineType,
  });

  const rowLabelHeader = report?.supplierTagTable.title.includes('Supplier')
    ? gt('Supplier', { context: 'rebates report row label column' })
    : gt('Category', { context: 'rebates report row label column' });

  const downloadFilename = `supplier-tag-report-${effectiveYear}`;
  const downloadOptions = useMemo(
    () =>
      report
        ? reportTableToDownloadOptions({
            table: report.supplierTagTable,
            filename: downloadFilename,
            timeFilter: effectiveTimeFilter,
            year: effectiveYear,
            rowLabelHeader,
          })
        : {
            data: [] as SupplierTagRow[],
            filename: downloadFilename,
            columns: [{ id: '_label', label: rowLabelHeader }],
          },
    [
      report,
      downloadFilename,
      effectiveTimeFilter,
      effectiveYear,
      rowLabelHeader,
    ],
  );
  const columns = useMemo(
    () => supplierTagColumns(downloadOptions),
    [downloadOptions],
  );
  const defaultSort = {
    sortBy: (columns[columns.length - 1]?.id ??
      '_label') as keyof SupplierTagRow,
    sortDirection: 'desc' as const,
  };
  const reportContent = (
    <>
      {/* Loading State */}
      {isLoading && (
        <Card>
          <CardHeader>
            <CardTitle>
              <T context="loading skeleton card title">
                Rebates by Supplier Tag
              </T>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <TableSkeleton />
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-destructive">
              <T context="error message when rebate report fails to load">
                Error loading report data. Please try again.
              </T>
            </p>
          </CardContent>
        </Card>
      )}

      {/* Report Table */}
      {report && !isLoading && (
        <SupplierTagReportTable
          table={report.supplierTagTable}
          tableName={
            isShared
              ? undefined
              : `${selectedSupplierTag?.label} ${gt('Suppliers', {
                  context: 'report table name suffix after supplier tag name',
                })}`
          }
          timeFilter={effectiveTimeFilter}
          onTimeFilterChange={setTimeFilter}
          downloadFilename={downloadFilename}
          timeFilterOptions={[
            {
              id: 'current',
              label: gt('Current', {
                context: 'report time filter, current period',
              }),
            },
            {
              id: 'ytd',
              label: gt('YTD', {
                context: 'report time filter, year to date',
              }),
            },
            ...(effectiveLineType === 'predictions'
              ? [
                  {
                    id: 'all',
                    label: gt('All', {
                      context: 'report time filter, all periods',
                    }),
                  },
                ]
              : []),
          ]}
          timePeriodPlaceholder={gt('Select time period...', {
            context: 'report time period dropdown',
          })}
          searchPlaceholder={gt('Search...', {
            context: 'report table search placeholder',
          })}
          columns={columns}
          defaultSort={defaultSort}
        />
      )}
    </>
  );

  const page = isShared ? (
    reportContent
  ) : (
    <DashboardLayout>
      <div className="p-1 w-full min-w-0 max-w-full overflow-hidden">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div>
            <HeaderPageTitle>
              <T context="report page title with year">
                Supplier Tag Summary - <Var>{selectedYear}</Var>
              </T>
            </HeaderPageTitle>
            <p className="text-sm text-muted-foreground">
              <T context="report page subtitle">
                View rebate summaries aggregated by supplier tags and months
              </T>
            </p>
          </div>
          <div className="flex gap-2">
            <SupplierTagExcelButton downloadOptions={downloadOptions} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <ShareNetworkIcon className="h-4 w-4 mr-2" />
                  <T context="button to open share options for report">Share</T>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setShareModalOpen(true)}>
                  <ShareNetworkIcon className="h-4 w-4 mr-2" />
                  <T context="menu item, create shareable report link">
                    Create share link
                  </T>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLinksManagerOpen(true)}>
                  <LinkIcon className="h-4 w-4 mr-2" />
                  <T context="menu item, manage existing shared links">
                    Manage shared links
                  </T>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Filters Card */}
        <Card className="mb-2">
          <CardHeader>
            <CardTitle>
              <T context="card title, report filter controls">Filters</T>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-3">
              <SingleSelect
                label={gt('Year', {
                  context: 'filter label, calendar year',
                })}
                labelPosition="left"
                options={yearOptions}
                value={
                  yearOptions.find((opt) => opt.value === selectedYear) || null
                }
                onChange={(option) => {
                  if (option) {
                    setSelectedYear(option.value);
                  }
                }}
                placeholder={gt('Select year...', {
                  context: 'year select placeholder',
                })}
                searchPlaceholder={gt('Search years...', {
                  context: 'search years in select',
                })}
                shouldAutoSort={false}
              />
              <MultiSelect
                label={gt('Divisions', {
                  context: 'filter label, business divisions',
                })}
                labelPosition="left"
                options={divisionOptions}
                value={selectedDivisions}
                onChange={setSelectedDivisions}
                placeholder={gt('Select divisions...', {
                  context: 'multi-select placeholder',
                })}
                searchPlaceholder={gt('Search divisions...', {
                  context: 'search inside division multi-select',
                })}
                disabled={divisionsLoading}
                hideChips={true}
              />
              <SingleSelect
                label={gt('Supplier Tag', {
                  context: 'filter label, supplier tag',
                })}
                labelPosition="left"
                options={supplierTagOptions}
                value={selectedSupplierTag}
                onChange={setSelectedSupplierTag}
                placeholder={gt('Select supplier tag...', {
                  context: 'supplier tag select placeholder',
                })}
                searchPlaceholder={gt('Search supplier tags...', {
                  context: 'search supplier tags in select',
                })}
                disabled={supplierTagsLoading}
              />
              <div>
                <SingleSelect
                  label={gt('Line Type', {
                    context: 'filter label, rebate line type',
                  })}
                  labelPosition="left"
                  options={lineTypeOptions}
                  value={
                    lineTypeOptions.find((opt) => opt.id === lineType) || null
                  }
                  onChange={(option) => {
                    if (option) {
                      const nextLineType = option.id as ReportLineType;
                      setLineType(nextLineType);
                      if (
                        nextLineType !== 'predictions' &&
                        timeFilter === 'all'
                      ) {
                        setTimeFilter('current');
                      }
                    }
                  }}
                  placeholder={gt('Select line type...', {
                    context: 'line type select placeholder',
                  })}
                  searchPlaceholder={gt('Search line types...', {
                    context: 'search line types in select',
                  })}
                />
                {lineType && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {LINE_TYPE_DESCRIPTIONS[lineType]}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {reportContent}
      </div>

      <ShareReportModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        reportId={REPORT_ID}
        reportTitle={reportTitle}
        initialYear={selectedYear}
        initialDivisions={selectedDivisions}
        initialLineType={lineType}
        customFilters={[
          {
            key: 'supplierTagIds',
            label: gt('Supplier Tags', {
              context: 'share modal filter label, supplier tags',
            }),
            options: supplierTagOptions.map((opt) => ({
              id: opt.id,
              label: opt.label,
            })),
            initialValue: selectedSupplierTag ? [selectedSupplierTag.id] : [],
            placeholder: gt('Select supplier tags...', {
              context: 'share modal supplier tag placeholder',
            }),
            receiverDisplayType: 'single',
          },
        ]}
      />
      <SharedLinksManager
        open={linksManagerOpen}
        onClose={() => setLinksManagerOpen(false)}
        reportId={REPORT_ID}
        reportTitle={reportTitle}
        onCreateNew={() => {
          setLinksManagerOpen(false);
          setShareModalOpen(true);
        }}
      />
    </DashboardLayout>
  );

  return (
    <SupplierTagSortProvider
      key={report ? 'loaded' : 'empty'}
      data={downloadOptions.data}
      columns={columns}
      defaultSort={defaultSort}
    >
      {page}
    </SupplierTagSortProvider>
  );
};

export default SupplierTagReportPage;
